import React, { FC, useEffect, useMemo } from 'react';
import partition from 'lodash/partition';
import {
  Card,
  CardPalette,
  GridLayout,
  GridLayoutColumn,
  Text,
  TextLink,
  Box,
  Button,
  SpinnerFullPage,
} from '@updater/ui-uds';
import {
  makeForwardMailReceiptsForVerticals,
  makeTVIReceiptsForVerticals,
} from 'flows/core/utils/card-makers';
import { ItemKinds } from 'types';
import { useDataForCards } from 'flows/core/hooks/use-data-for-cards';
import { useReservation } from 'flows/tvi/hooks/use-reservation';
import { findMostRecentTVIByStatus } from 'flows/core/utils/card-makers/utils';
import { deepLinkStatuses } from 'flows/core/hooks/constants';
import { BasicReceiptCard } from 'flows/core/components';
import Link from 'next/link';
import styled from '@emotion/styled';
import { PageTemplate, ProfileTemplateProps } from 'components/page-template';
import { cardsPriority, sortByStatus } from 'utils/cards';
import { useRouter } from 'next/router';
import { ReceiptPayload } from 'flows/core/types/cards';
import { useTracking } from 'react-tracking';

import { FYMReceipts } from './FYMReceipts';
import { FILE_NEW_FORM } from '../../flows/forward-mail/constants/copy';
import { useFYMStatus } from 'flows/core/hooks/use-user-move-type';

/**
 * @module VerticalDetails
 * @description Interstitial component that presents next steps and done items for a given flow.
 * @returns {React.ReactElement}
 */

type VerticalDetailsTypes =
  | `${ItemKinds.FORWARD_MAIL}`
  | `${ItemKinds.TV_INTERNET}`;

interface VerticalDetailsProps {
  headline: ProfileTemplateProps['title'];
  subtitle?: ProfileTemplateProps['subtitle'];
  type: VerticalDetailsTypes;
  target: string;
  actionTracker?: () => void;
  startOverPath: string;
  startOverText?: string;
}

const FileNewFormButton = styled(Button)`
  height: 64px;
  width: 100%;
  border-radius: ${({ theme }) => theme.radii.s};
`;

const StatusCard = styled(Card)`
  border: none;
  padding: 16px 24px;
  margin-top: 1rem;
`;

const cardFactory = {
  forward_mail: makeForwardMailReceiptsForVerticals,
  tv_internet: makeTVIReceiptsForVerticals,
};

export const VerticalDetails: FC<VerticalDetailsProps> = ({
  headline,
  subtitle,
  type,
  target,
  actionTracker,
  startOverPath,
  startOverText = 'Start over',
}) => {
  const { trackEvent } = useTracking();
  const router = useRouter();
  const cardQuery = useDataForCards();
  const tviOfferQuery = useReservation();
  const { showFYM } = useFYMStatus();

  const cards = useMemo(() => {
    let [submitted, active] = [[], []] as ReceiptPayload[][];

    if (
      !cardQuery.loading &&
      tviOfferQuery.status !== 'loading' &&
      cardFactory[type] != null
    ) {
      [submitted, active] = partition(
        cardQuery.data?.items,
        (item) =>
          deepLinkStatuses.includes(item.status as string) &&
          item.type !== 'promotion' &&
          item.type.includes(type)
      ).map((items) =>
        sortByStatus(
          cardFactory[type]({
            items,
            tviData: tviOfferQuery.reservation ?? undefined,
            target,
          }),
          cardsPriority
        )
      );
    }

    return { submitted, active };
  }, [type, target, cardQuery, tviOfferQuery]);

  const onStartOverClick = () => {
    trackEvent({
      object: 'restart_flow',
      details: {
        type,
      },
      verb: 'clicked',
    });
  };

  useEffect(() => {
    const tviItems = cardQuery.data?.items.filter(
      (item) => item.type === ItemKinds.TV_INTERNET
    );
    const mostRecentTVI = findMostRecentTVIByStatus(tviItems || []);
    // conditional redirects for TVI when no offer is present or when offer has already been claimed/cancelled
    if (
      type === ItemKinds.TV_INTERNET &&
      tviOfferQuery.status !== 'loading' &&
      !cardQuery.loading &&
      // when no offer is present OR it has been cancelled
      (!tviOfferQuery?.reservation?.offer ||
        mostRecentTVI?.status === 'cancelled')
    ) {
      router.replace(cards.submitted.length > 0 ? target : startOverPath);
    }
  }, [type, target, startOverPath, router, cards, tviOfferQuery, cardQuery]);

  return cardQuery.loading ? (
    <Box width="100%">
      <SpinnerFullPage />
    </Box>
  ) : (
    <PageTemplate
      maxWidth={null}
      error={cardQuery.error}
      title={headline}
      subtitle={subtitle}
      includeBackButton
    >
      {/* For FYM user can only have 2 so only show button if cards < 2 */}
      {cards.active.length < 2 &&
        type === ItemKinds.FORWARD_MAIL &&
        showFYM && (
          <GridLayout
            style={{ margin: 'none', paddingTop: 'm', paddingBottom: '.75rem' }}
          >
            <GridLayoutColumn span={['4', null, '2', '6']}>
              <FileNewFormButton onClick={() => router.push(startOverPath)}>
                <Text
                  as="p"
                  variant="s"
                  color="white"
                  data-testid="file-new-form-text"
                >
                  {FILE_NEW_FORM}
                </Text>
              </FileNewFormButton>
            </GridLayoutColumn>
          </GridLayout>
        )}

      <GridLayout style={{ margin: '0' }}>
        <GridLayoutColumn span={['4', null, '3', '6']}>
          {cards.active.length > 0 &&
            cards.active.map((activeCard) => (
              <Box mb="s" key={activeCard.identifier}>
                <BasicReceiptCard
                  {...activeCard}
                  actionTracker={actionTracker}
                />
                {Boolean(startOverPath) && (
                  <TextLink
                    variant="m"
                    mt="s"
                    onClick={onStartOverClick}
                    data-testid="start-over-text-link"
                  >
                    <Link href={startOverPath} passHref>
                      {startOverText}
                    </Link>
                  </TextLink>
                )}
              </Box>
            ))}

          {cards.submitted.length > 0 && (
            <>
              {type === ItemKinds.FORWARD_MAIL ? (
                <>
                  <FYMReceipts />{' '}
                  {!showFYM && (
                    <GridLayout style={{ margin: '0', paddingTop: '8px' }}>
                      <GridLayoutColumn span={['4', null, '2', '6']}>
                        <FileNewFormButton
                          data-testid="file-new-form-btn"
                          onClick={() => router.push('/')}
                        >
                          <Text as="p" variant="s" color="white">
                            All set!
                          </Text>
                        </FileNewFormButton>
                      </GridLayoutColumn>
                    </GridLayout>
                  )}
                </>
              ) : (
                <>
                  {cards.submitted.map((submittedCard) => (
                    <Box mb="s" key={submittedCard.identifier}>
                      <BasicReceiptCard
                        {...submittedCard}
                        showSubtitle
                        actionTracker={actionTracker}
                      />
                    </Box>
                  ))}
                </>
              )}
            </>
          )}

          {cards.active.length > 0 && type === ItemKinds.TV_INTERNET && (
            <StatusCard variant="rounded" backgroundColor={CardPalette.gray96}>
              <Text as="p" variant="s">
                Purchased a plan over the phone? Don’t worry — we’re on it.
                Check back in 24 hr for confirmation.
              </Text>
            </StatusCard>
          )}
        </GridLayoutColumn>
      </GridLayout>
    </PageTemplate>
  );
};
